import Box from '@liquidweb-dev/undercurrent/Box';
import ArrowUpDownIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowUpDown';
import CircleDashedIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleDashed';
import CloudIcon from '@liquidweb-dev/undercurrent/icons/lucide/Cloud';
import GlobeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Globe';
import WaypointsIcon from '@liquidweb-dev/undercurrent/icons/lucide/Waypoints';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import { LucideSvgProps } from '@liquidweb-dev/undercurrent/types/svgTypes';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import DetermineCountryFlag from 'components/CountryFlag';
import useBackups from 'components/TotalBackupsBlock/useBackups';
import useAssetNetworkPrivateVpnDetails from 'modules/queries/asset/network/private/vpn/useDetails';
import useAssetDetails from 'modules/queries/asset/useDetails';
import useCloudBackupList from 'modules/queries/cloud/backup/useList';
import { dataCenterMap } from 'modules/regions';
import AddonCard from './AddonCard';
import AddonWrapper from './AddonWrapper';

interface TextIconProps {
	icon: React.ComponentType<LucideSvgProps>;
	label: string | React.ReactNode;
	isTextMain?: boolean;
}

interface CountryTextProps {
	countryFlag: string | undefined;
	location: string | undefined;
}

interface AddOnProps {
	uniqId: string;
	domain?: string;
}

const IconWithText = ({
	icon: Icon,
	label,
	isTextMain = false,
}: TextIconProps) => {
	return (
		<Stack
			sx={{
				flexDirection: 'row',
				alignItems: 'center',
				gap: 50,
			}}
		>
			<Icon
				sx={(theme) => ({
					width: theme.size.xS,
					height: theme.size.xS,
					color: theme.vars.palette.uc.icon.weak,
				})}
			/>
			<Typography
				variant="label-lg"
				sx={(theme) => ({
					color: isTextMain
						? theme.vars.palette.uc.text.main
						: theme.vars.palette.uc.text.weak,
					fontWeight: 500,
				})}
			>
				{label}
			</Typography>
		</Stack>
	);
};

const CountryWithText = ({ countryFlag, location }: CountryTextProps) => {
	return (
		<Stack
			sx={{
				flexDirection: 'row',
				alignItems: 'center',
				gap: 50,
			}}
		>
			<DetermineCountryFlag
				country={countryFlag}
				sx={(theme) => ({
					width: theme.size.xS,
					height: theme.size.xS,
				})}
			/>
			<Typography
				variant="label-lg"
				sx={(theme) => ({
					color: theme.vars.palette.uc.text.main,
					fontWeight: 500,
				})}
			>
				{location}
			</Typography>
		</Stack>
	);
};

export const ThreatDownAddon = () => {
	return (
		<AddonWrapper>
			<AddonCard
				title="ThreatDown: Managed detection and response"
				info={
					<Typography
						variant="body-sm"
						sx={(theme) => ({
							color: theme.vars.palette.uc.text.weak,
						})}
					>
						Combines the power of EDR and MDR to protect against today’s most
						evasive threats.
					</Typography>
				}
				addOnType="ServerSecurity.ThreatDown"
			/>
		</AddonWrapper>
	);
};

export const AcronisEdrAddon = () => {
	return (
		<AddonWrapper>
			<AddonCard
				title="Endpoint Detection & Response powered by Acronis"
				info={
					<Typography
						variant="body-sm"
						sx={(theme) => ({
							color: theme.vars.palette.uc.text.weak,
						})}
					>
						Protect your endpoints from sophisticated threats with proactive
						detection and response
					</Typography>
				}
				addOnType="Acronis"
			/>
		</AddonWrapper>
	);
};

export const AdvancedDDoSAddon = () => {
	return (
		<AddonWrapper>
			<AddonCard
				title="Advanced DDoS Protection"
				info={
					<Typography
						variant="body-sm"
						sx={({ vars }) => ({
							color: vars.palette.uc.text.weak,
						})}
					>
						Better protection against Layer 4 Attacks that’s volumetric up to 10
						GBps
					</Typography>
				}
				addOnType="DDoS.Server.Protect"
			/>
		</AddonWrapper>
	);
};

export const Imunify360Addon = () => {
	return (
		<AddonWrapper>
			<AddonCard
				title="Imunify360: Malware detection and remediation"
				info={
					<Typography
						variant="body-sm"
						sx={(theme) => ({
							color: theme.vars.palette.uc.text.weak,
						})}
					>
						Antivirus, Firewall, WAF, PHP Security Layer, Patch Management,
						Domain Reputation with automation.
					</Typography>
				}
				infoContainerProps={{
					direction: { xs: 'column', md: 'row' },
				}}
				addOnType="ServerSecurity.Imunify360"
			/>
		</AddonWrapper>
	);
};

export const AcronisBackupAddon = ({ uniqId }: AddOnProps) => {
	const { data: acronisAssetDetails, isLoading } = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: ['acronisBackingUp'],
		},
		{ enabled: Boolean(uniqId) },
	);

	const hostnames =
		acronisAssetDetails?.acronisBackingUp
			?.map((item: { domain: string }) => item.domain)
			?.join('') ?? '';

	const title = `Acronis Backups: ${hostnames}`;

	return (
		<AddonWrapper>
			<AddonCard loading={isLoading} title={title} addOnType="Acronis" />
		</AddonWrapper>
	);
};

export const CloudBackUpAddon = ({ uniqId, domain }: AddOnProps) => {
	const title = `Cloud Backups: ${domain}`;
	const { isLoading, data: stormBackups } = useCloudBackupList(
		{
			uniq_id: uniqId,
			page_size: 1,
		},
		{ enabled: Boolean(uniqId) },
	);
	const { totalSize } = useBackups(uniqId);
	const backupCount = stormBackups?.item_total;
	const backupUsed = totalSize || '0 GB';

	return (
		<AddonWrapper>
			<AddonCard
				title={title}
				loading={isLoading}
				info={
					<>
						<Typography
							variant="body-sm"
							sx={({ vars }) => ({
								color: vars.palette.uc.text.weak,
							})}
						>
							Backup count: {backupCount}, &nbsp;
						</Typography>
						<Typography
							variant="body-sm"
							sx={({ vars }) => ({
								color: vars.palette.uc.text.weak,
							})}
						>
							Current space used: {backupUsed}
						</Typography>
					</>
				}
				infoContainerProps={{
					direction: { xs: 'column', md: 'row' },
				}}
				addOnType="Cloud Backup"
			/>
		</AddonWrapper>
	);
};

export const CloudVPNService = ({ domain }: AddOnProps) => {
	const title = `Cloud VPN: ${domain}`;
	return (
		<AddonWrapper>
			<AddonCard title={title} addOnType="Cloud.VPN" />
		</AddonWrapper>
	);
};

export const VirtualIPService = ({ uniqId }: AddOnProps) => {
	const { isLoading, data: virtualIPsData } = useAssetDetails(
		{
			uniq_id: uniqId ?? '',
			alsowith: ['networkSummary'],
		},
		{ enabled: Boolean(uniqId) },
	);

	const primaryIP = virtualIPsData?.networkSummary?.primary_ip || '';
	const privateIP = virtualIPsData?.networkSummary?.private_ip || '';
	const title = `Virtual IP: ${virtualIPsData?.domain}`;

	return (
		<AddonWrapper>
			<AddonCard
				loading={isLoading}
				title={title}
				info={
					<>
						<Typography
							variant="body-sm"
							sx={(theme) => ({
								color: theme.vars.palette.uc.text.weak,
							})}
						>
							Primary IP: {primaryIP}, &nbsp;
						</Typography>
						<Typography
							variant="body-sm"
							sx={(theme) => ({
								color: theme.vars.palette.uc.text.weak,
							})}
						>
							Private IP: {privateIP}
						</Typography>
					</>
				}
				infoContainerProps={{
					direction: { xs: 'column', md: 'row' },
				}}
				addOnType="SS.VIP"
			/>
		</AddonWrapper>
	);
};

export const LoadBalancerService = ({ uniqId }: AddOnProps) => {
	const { isLoading, data } = useAssetDetails(
		{
			alsowith: ['loadbalancer'],
			uniq_id: uniqId,
		},
		{ enabled: Boolean(uniqId) },
	);
	const title = `Load Balancer: ${data?.loadbalancer?.name}`;

	return (
		<AddonWrapper>
			<AddonCard
				loading={isLoading}
				title={title}
				info={
					<Stack
						direction={{ xs: 'column', sm: 'row' }}
						sx={{
							gap: { xs: 100, sm: 200 },
							alignItems: { xs: 'flex-start', sm: 'center' },
						}}
					>
						<CountryWithText
							countryFlag={data?.country || ''}
							location={data?.location}
						/>
						<IconWithText icon={GlobeIcon} label={data?.ip || ''} isTextMain />
						<IconWithText
							icon={CloudIcon}
							label="Cloud.LoadBalancer"
							isTextMain
						/>
					</Stack>
				}
				infoContainerProps={{
					direction: { xs: 'column', md: 'row' },
				}}
				addOnType="Cloud.LoadBalancer"
			/>
		</AddonWrapper>
	);
};

export const ObjectStorageService = ({ uniqId }: { uniqId: string }) => {
	const title = `Object Storage: ${uniqId}`;
	return (
		<AddonWrapper>
			<AddonCard
				title={title}
				infoContainerProps={{
					direction: { xs: 'column', md: 'row' },
				}}
				addOnType="SS.ObjectStore"
			/>
		</AddonWrapper>
	);
};

export const IPPoolService = ({ domain, uniqId }: AddOnProps) => {
	const title = `IP Pool: ${domain}`;
	const { data: ipPoolAsset, isLoading: isLoadingAssetDetails } =
		useAssetDetails(
			{
				uniq_id: uniqId,
				alsowith: ['featureDetails', 'zone'],
			},
			{
				enabled: Boolean(uniqId),
			},
		);

	const regionId = ipPoolAsset?.region_id;
	const country = ipPoolAsset?.country;
	const location = ipPoolAsset?.location;
	const usedIps = ipPoolAsset?.featureDetails?.UsedPoolIP?.num_units ?? 0;
	const unusedIps = ipPoolAsset?.featureDetails?.UnUsedPoolIP?.num_units ?? 0;

	return (
		<AddonWrapper>
			<AddonCard
				title={title}
				loading={isLoadingAssetDetails}
				info={
					<>
						{regionId && (
							<CountryWithText
								countryFlag={country || ''}
								location={location}
							/>
						)}
						<IconWithText
							icon={GlobeIcon}
							label={`${usedIps} Used IPs`}
							isTextMain
						/>
						<IconWithText
							icon={CircleDashedIcon}
							label={`${unusedIps} Unused IPs`}
							isTextMain
						/>
					</>
				}
				infoContainerProps={{
					direction: { xs: 'column', md: 'row' },
					sx: {
						alignItems: 'flex-start',
						gap: 200,
						paddingTop: 100,
					},
				}}
				addOnType="SS.IP"
			/>
		</AddonWrapper>
	);
};

export const FirewallService = ({ uniqId, domain }: AddOnProps) => {
	const title = `Firewall: ${domain}`;
	const { data: firewalldetails, isLoading } = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: ['featureDetails', 'region'],
		},
		{ enabled: Boolean(uniqId) },
	);

	return (
		<AddonWrapper>
			<AddonCard
				title={title}
				loading={isLoading}
				info={
					<>
						{firewalldetails?.region_id && (
							<CountryWithText
								countryFlag={firewalldetails?.country}
								location={firewalldetails?.location}
							/>
						)}
						<IconWithText
							icon={GlobeIcon}
							label={firewalldetails?.ip}
							isTextMain
						/>
					</>
				}
				infoContainerProps={{
					direction: { xs: 'column', md: 'row' },
					sx: { alignItems: 'flex-start', gap: 150 },
				}}
				addOnType="Firewall"
			/>
		</AddonWrapper>
	);
};

export const PrivateVPNService = ({ uniqId }: AddOnProps) => {
	const { data: vpnDetails, isLoading: isLoadingVpnDetails } =
		useAssetNetworkPrivateVpnDetails({
			uniq_id: uniqId,
			alsowith: ['asset', ['asset', 'featureDetails']],
		});

	const {
		asset,
		connectionTypeDisplayTruncated,
		rateLimitDisplay,
		rateLimitPrice,
		vpnTypeDisplay,
	} = vpnDetails || {};

	const title = `${vpnTypeDisplay} VPN`;
	const addonType = 'Networking.Private.VPN';
	const regionId = asset?.region_id;

	const { label: regionLabel, country } = dataCenterMap[regionId || 1];

	return (
		<AddonWrapper>
			<AddonCard
				loading={isLoadingVpnDetails}
				title={title}
				addOnType={addonType}
				info={
					<Stack sx={{ flexDirection: 'row', gap: 200, alignItems: 'center' }}>
						<CountryWithText countryFlag={country} location={regionLabel} />
						<IconWithText
							icon={WaypointsIcon}
							label={connectionTypeDisplayTruncated}
							isTextMain
						/>
						<IconWithText
							icon={ArrowUpDownIcon}
							label={
								<>
									{rateLimitDisplay}{' '}
									<Box
										component="span"
										sx={({ vars }) => ({
											color: vars.palette.uc.success.text.main,
										})}
									>
										(${rateLimitPrice}/mo)
									</Box>
								</>
							}
							isTextMain
						/>
					</Stack>
				}
			/>
		</AddonWrapper>
	);
};
